import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import {
  cancelSubscription,
  fetchBillingStatus,
  formatBillingError,
  keepSubscription,
  skipTrial,
} from "../Api/billing";
import GlassConfirmationModal from "../Components/Common/Modals/GlassConfirmationModal";
import {
  BILLING_CONFIRMATIONS,
  BillingConfirmationKind,
} from "../Constants/billingConfirmations";
import { IBillingStatus } from "../Types/onboarding";
import { refreshUserAfterBillingChange } from "../utils/clientServiceStatus";
import { isSubscriptionCancelScheduled } from "../utils/billingSubscription";

type TrialBannerContextValue = {
  isTrialing: boolean;
  isLoading: boolean;
  isCollapsed: boolean;
  status: IBillingStatus | null;
  actionLoading: boolean;
  feedbackMessage: string;
  daysLabel: string;
  collapse: () => void;
  expand: () => void;
  reload: () => Promise<void>;
  skipTrial: () => Promise<void>;
  cancelTrial: () => Promise<void>;
  keepTrial: () => Promise<void>;
};

const noopAsync = async () => {};

const defaultValue: TrialBannerContextValue = {
  isTrialing: false,
  isLoading: false,
  isCollapsed: false,
  status: null,
  actionLoading: false,
  feedbackMessage: "",
  daysLabel: "",
  collapse: () => {},
  expand: () => {},
  reload: noopAsync,
  skipTrial: noopAsync,
  cancelTrial: noopAsync,
  keepTrial: noopAsync,
};

const TrialBannerContext = createContext<TrialBannerContextValue>(defaultValue);

function collapseStorageKey(clientId: string): string {
  return `intentico:trialBannerCollapsed:${clientId}`;
}

type TrialBannerProviderProps = {
  children: React.ReactNode;
  enabled: boolean;
  clientId: string | null | undefined;
};

export const TrialBannerProvider: React.FC<TrialBannerProviderProps> = ({
  children,
  enabled,
  clientId,
}) => {
  const [status, setStatus] = useState<IBillingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<BillingConfirmationKind | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    if (!enabled || !clientId) {
      setStatus(null);
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchBillingStatus(clientId);
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!clientId) {
      setIsCollapsed(false);
      return;
    }
    try {
      setIsCollapsed(localStorage.getItem(collapseStorageKey(clientId)) === "1");
    } catch {
      setIsCollapsed(false);
    }
  }, [clientId]);

  const persistCollapsed = useCallback(
    (collapsed: boolean) => {
      setIsCollapsed(collapsed);
      if (!clientId) return;
      try {
        localStorage.setItem(collapseStorageKey(clientId), collapsed ? "1" : "0");
      } catch {
        /* ignore storage errors */
      }
    },
    [clientId]
  );

  const collapse = useCallback(() => persistCollapsed(true), [persistCollapsed]);
  const expand = useCallback(() => persistCollapsed(false), [persistCollapsed]);

  const isTrialing = Boolean(enabled && clientId && status?.is_trialing);

  const daysLabel = useMemo(() => {
    if (status?.trial_days_remaining == null) return "Trial active";
    const days = status.trial_days_remaining;
    return `${days} day${days === 1 ? "" : "s"} left`;
  }, [status?.trial_days_remaining]);

  const handleSkipTrial = useCallback(() => {
    setPendingConfirm("skipTrial");
  }, []);

  const handleCancelTrial = useCallback(() => {
    setPendingConfirm("cancelTrial");
  }, []);

  const dismissConfirm = useCallback(() => {
    if (!actionLoading) {
      setPendingConfirm(null);
    }
  }, [actionLoading]);

  const handleConfirmAction = useCallback(async () => {
    if (!clientId || !pendingConfirm) return;

    setActionLoading(true);
    setFeedbackMessage("");

    try {
      const msg =
        pendingConfirm === "skipTrial"
          ? await skipTrial(clientId)
          : await cancelSubscription(clientId);
      setFeedbackMessage(msg);
      setPendingConfirm(null);
      await reload();
      if (pendingConfirm === "skipTrial" || pendingConfirm === "cancelTrial") {
        await refreshUserAfterBillingChange(dispatch, navigate);
      }
    } catch (err) {
      setFeedbackMessage(formatBillingError(err));
    } finally {
      setActionLoading(false);
    }
  }, [clientId, dispatch, navigate, pendingConfirm, reload]);

  const handleKeepTrial = useCallback(async () => {
    if (!clientId) return;

    setActionLoading(true);
    setFeedbackMessage("");

    try {
      const msg = await keepSubscription(clientId);
      setFeedbackMessage(msg);
      await reload();
      await refreshUserAfterBillingChange(dispatch, navigate);
    } catch (err) {
      setFeedbackMessage(formatBillingError(err));
    } finally {
      setActionLoading(false);
    }
  }, [clientId, dispatch, navigate, reload]);

  const confirmationCopy = pendingConfirm ? BILLING_CONFIRMATIONS[pendingConfirm] : null;

  const value = useMemo(
    (): TrialBannerContextValue => ({
      isTrialing,
      isLoading,
      isCollapsed,
      status,
      actionLoading,
      feedbackMessage,
      daysLabel,
      collapse,
      expand,
      reload,
      skipTrial: handleSkipTrial,
      cancelTrial: handleCancelTrial,
      keepTrial: handleKeepTrial,
    }),
    [
      actionLoading,
      collapse,
      daysLabel,
      expand,
      feedbackMessage,
      handleCancelTrial,
      handleKeepTrial,
      handleSkipTrial,
      isCollapsed,
      isLoading,
      isTrialing,
      reload,
      status,
    ]
  );

  return (
    <TrialBannerContext.Provider value={value}>
      {children}
      {confirmationCopy ? (
        <GlassConfirmationModal
          isOpen
          onClose={dismissConfirm}
          onConfirm={() => void handleConfirmAction()}
          title={confirmationCopy.title}
          message={confirmationCopy.message}
          warningMessage={confirmationCopy.warningMessage}
          confirmText={confirmationCopy.confirmText}
          variant={confirmationCopy.variant}
          kicker="Free trial"
          isLoading={actionLoading}
          loadingText="Processing…"
        />
      ) : null}
    </TrialBannerContext.Provider>
  );
};

export function useTrialBanner(): TrialBannerContextValue {
  return useContext(TrialBannerContext);
}
